import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { revenueData, volumeChartData } from "../../../mocks/cryptoAdmin";

const userGrowth = [
  { month: "Jan", users: 4200 }, { month: "Feb", users: 6800 },
  { month: "Mar", users: 9100 }, { month: "Apr", users: 11400 },
  { month: "May", users: 13900 }, { month: "Jun", users: 15800 },
  { month: "Jul", users: 17200 }, { month: "Aug", users: 19100 },
  { month: "Sep", users: 20800 }, { month: "Oct", users: 22300 },
  { month: "Nov", users: 23600 }, { month: "Dec", users: 24817 },
];

const assetDist = [
  { name: "USDT", value: 4350, color: "#00C896" },
  { name: "BTC", value: 474, color: "#f59e0b" },
  { name: "ETH", value: 741, color: "#60a5fa" },
  { name: "BNB", value: 775, color: "#f87171" },
  { name: "SOL", value: 730, color: "#a78bfa" },
  { name: "XRP", value: 405, color: "#fb923c" },
];

const kpiCards = [
  { label: "Avg. Session Duration", value: "4m 32s", change: "+12%", up: true },
  { label: "Daily Active Users", value: "8,241", change: "+5.4%", up: true },
  { label: "Avg. Trade Size", value: "₦487,000", change: "+8.1%", up: true },
  { label: "Churn Rate (30d)", value: "1.2%", change: "-0.3%", up: true },
];

const tooltipStyle = {
  backgroundColor: "#1a1a1a",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "10px",
  fontSize: "12px",
  color: "#fff",
};

export default function AnalyticsView() {
  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((k) => (
          <div key={k.label} className="bg-[#161616] border border-white/5 rounded-xl p-4">
            <p className="text-xs text-white/40">{k.label}</p>
            <p className="text-xl font-bold text-white mt-1">{k.value}</p>
            <span className={`text-xs font-semibold ${k.up ? "text-[#00C896]" : "text-rose-400"}`}>{k.change}</span>
          </div>
        ))}
      </div>

      {/* User growth */}
      <div className="bg-[#161616] border border-white/5 rounded-xl p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-white">User Growth</h3>
          <p className="text-xs text-white/30 mt-0.5">Cumulative registered users — 2026</p>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={userGrowth} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="ugGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00C896" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#00C896" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.3)" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.3)" }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "rgba(255,255,255,0.05)" }} />
              <Area type="monotone" dataKey="users" stroke="#00C896" strokeWidth={2} fill="url(#ugGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue + Asset dist */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#161616] border border-white/5 rounded-xl p-5">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-white">Monthly Revenue Breakdown</h3>
            <p className="text-xs text-white/30 mt-0.5">Fees vs spread income (₦ millions)</p>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }} barSize={10} barGap={3}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.3)" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.3)" }} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} formatter={(v) => [`₦${(v / 1000000).toFixed(1)}M`]} />
                <Bar dataKey="fees" fill="#00C896" radius={[4, 4, 0, 0]} name="Fees" />
                <Bar dataKey="spread" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Spread" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#161616] border border-white/5 rounded-xl p-5">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-white">AUM by Asset</h3>
            <p className="text-xs text-white/30 mt-0.5">₦ millions</p>
          </div>
          <div className="h-40 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={assetDist} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                  {assetDist.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`₦${v}M`]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {assetDist.map((a) => (
              <div key={a.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
                  <span className="text-xs text-white/50">{a.name}</span>
                </div>
                <span className="text-xs font-semibold text-white">₦{a.value}M</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Volume chart */}
      <div className="bg-[#161616] border border-white/5 rounded-xl p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-white">Weekly Transaction Volume</h3>
          <p className="text-xs text-white/30 mt-0.5">Buy / Sell / Deposit / Withdraw — ₦ millions</p>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={volumeChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }} barSize={8} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.3)" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgba(255,255,255,0.3)" }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="buy" fill="#00C896" radius={[4, 4, 0, 0]} name="Buy" />
              <Bar dataKey="sell" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Sell" />
              <Bar dataKey="deposit" fill="#60a5fa" radius={[4, 4, 0, 0]} name="Deposit" />
              <Bar dataKey="withdraw" fill="#f87171" radius={[4, 4, 0, 0]} name="Withdraw" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}